import { useTranslation } from '@dukanti/shared';
import { cn } from '@/lib/utils';

export interface StoreStatusBadgeProps {
  /** The publish state of the store. `draft` is the initial state after
   *  store creation; `published` is the live state after a successful
   *  `publish_store(_, true)` RPC. */
  readonly status: 'draft' | 'published';
  /** Optional extra classes — the parent usually passes `ms-auto` to
   *  anchor the badge to the end edge of a flex row. The component
   *  itself is direction-agnostic. */
  readonly className?: string;
  /** Optional testid prefix so containers that embed the badge can
   *  disambiguate when multiple instances coexist on the same page
   *  (e.g. the Dashboard header card + published-store-card both render
   *  this component). Defaults to `store-status-badge`, emitting
   *  `store-status-badge-draft` / `store-status-badge-published`.
   *  Pass a scoped prefix like `published-store-card-status-badge` to
   *  emit `published-store-card-status-badge-draft` /
   *  `published-store-card-status-badge-published`.
   *  See F-027 Phase 4.5 FIX 2. */
  readonly testIdPrefix?: string;
}

/**
 * Tiny pill showing the merchant-facing publish state of a store.
 *
 * The pill is 4px radius (per DESIGN.md §Badges & Pills — pills are
 * rectangular-soft, NOT fully rounded, to match Dukanti's "structured
 * warmth"). Draft is neutral Sand; Published is Sage with a live dot to
 * reinforce the "live on the internet" signal without shouting.
 *
 * Accessibility:
 *   - `role="status"` so assistive tech announces the state change when
 *     the parent re-renders after a publish/unpublish transition.
 *   - The visible label is the translated text, so the color carries no
 *     information on its own (WCAG 1.4.1).
 *   - `aria-label` falls back to the same translation so the visual pill
 *     and the accessible name stay in sync even if CSS strips the text.
 *
 * Test-ids (F-004 design §5.5 — variant-suffixed so AC8's E2E test can
 * assert the published variant exactly):
 *   - `store-status-badge-draft` on the draft variant
 *   - `store-status-badge-published` on the published variant
 *
 * Note: the transient `data-testid="store-status-loading"` element is
 * owned by the PARENT card (dashboard integration in Phase 4d), not this
 * badge — that element renders while `usePublishStore.isPending` is true.
 * This component is pure and always renders a final-state pill.
 */
export function StoreStatusBadge({
  status,
  className,
  testIdPrefix = 'store-status-badge',
}: StoreStatusBadgeProps) {
  const { t } = useTranslation();

  // Lookup the translated label once. Both `aria-label` and the visible
  // text use it so a screen-reader user hears the same thing a sighted
  // user sees.
  const label =
    status === 'published'
      ? t('admin.publishStore.badge.published')
      : t('admin.publishStore.badge.draft');

  // Variant-specific tokens. Colors come from the Dukanti preset
  // (`sand-*`, `sage`) — no hex literals.
  //   - Draft: sand-200 background, sand-600 text.
  //   - Published: sage at 15% opacity background, sage text.
  // Both use the shared pill shape (4px radius, 2px 8px padding, Inter
  // 500 11px tracking 0.06em) defined inline.
  const variantClasses =
    status === 'published' ? 'bg-sage/15 text-sage' : 'bg-sand-200 text-sand-600';

  const testId = status === 'published' ? `${testIdPrefix}-published` : `${testIdPrefix}-draft`;

  return (
    <span
      role="status"
      aria-label={label}
      data-testid={testId}
      className={cn(
        // Shape: inline-flex so the optional live-dot aligns with the
        // label baseline. rounded = 4px via Tailwind's default `rounded`
        // utility. Padding matches the Stitch spec (2px block, 8px inline).
        'inline-flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5',
        // Typography: Inter 500 (the sans stack in the preset), 11px,
        // 16px line-height, 0.06em tracking, for the quiet uppercase
        // pill look from DESIGN.md.
        'font-sans text-[11px] font-medium leading-4 tracking-[0.06em]',
        variantClasses,
        className,
      )}
    >
      {status === 'published' ? (
        // Live dot — 6px circle in sage. `aria-hidden` because the
        // badge label already names the state; the dot is decorative.
        <span aria-hidden="true" className="bg-sage inline-block size-1.5 rounded-full" />
      ) : null}
      {label}
    </span>
  );
}
